import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import useUI from 'src/hooks/use-ui';
import { useGetPublicContextSessionQuery } from 'types/graphql';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';

import { ModalContainer } from 'shared/modals';
import { KioskLoginOptions } from 'src/components/kiosk-pro/kiosk-login/kiosk-login-options';
import { KioskLoginContinue } from 'src/components/kiosk-pro/kiosk-login/kiosk-login-continue';
import { useGetSessionId } from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-id';
import { usePublicSessionLoginHandler } from 'src/components/kiosk-pro/kiosk-pro-shared/use-public-session-login-handler';
import { usePublicContextSessionPusher } from 'src/hooks/use-public-context-session-pusher';

type KioskLoginModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  consumerPhoneLoginUrl: string;
  phoneLoginUrl: string;
};

export function KioskLoginModal({
  isOpen,
  handleClose,
  consumerPhoneLoginUrl,
  phoneLoginUrl,
}: KioskLoginModalProps): JSX.Element {
  const UI = useUI();
  const router = useRouter();
  const publicLoginSessionId = useGetSessionId();

  const { data, refetch } = useGetPublicContextSessionQuery({
    variables: {
      id: publicLoginSessionId,
    },
    pollInterval: 15000,
  });

  const [isLoginMethodVerified, setIsLoginMethodVerified] = useState(false);

  const { isUserLoggedInSuccessfully, userFirstName } = usePublicSessionLoginHandler(publicLoginSessionId);

  const updateLoginMethodVerified = useCallback((newValue: boolean | null) => {
    const value = newValue ?? false;
    setIsLoginMethodVerified(value);
  }, []);

  usePublicContextSessionPusher({
    sessionId: publicLoginSessionId,
    enabled: isOpen && !!publicLoginSessionId,
    onSessionUpdated: (sessionData) => {
      updateLoginMethodVerified(sessionData.loginMethodVerified);
      void refetch();
    },
    onLoginMethodVerified: (loginData) => {
      updateLoginMethodVerified(loginData.loginMethodVerified);
    },
  });

  const handleCloseModal = useCallback((): void => {
    UI.setIsKioskProLoginModalOpen(false);
    handleClose();

    const queryWithoutSessionId = { ...router.query };
    delete queryWithoutSessionId['session-id'];

    void router.replace(
      {
        pathname: router.pathname,
        query: queryWithoutSessionId,
      },
      undefined,
      { shallow: true }
    );
  }, [UI, handleClose, router]);

  useEffect(() => {
    if (data?.getPublicContextSession?.loginMethodVerified !== undefined && !isLoginMethodVerified) {
      setIsLoginMethodVerified(data.getPublicContextSession.loginMethodVerified ?? false);
    }
  }, [data, isLoginMethodVerified]);

  useEffect(() => {
    if (!isUserLoggedInSuccessfully) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      handleCloseModal();
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, [isUserLoggedInSuccessfully, handleCloseModal]);

  return (
    <StyledModalContainer
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      width='820px'
      alignItems='center'
      padding='40px'
      borderRadius='20px'
      ariaHideApp={false}
    >
      {isLoginMethodVerified ? (
        <KioskLoginContinue isUserLoggedInSuccessfully={isUserLoggedInSuccessfully} userFirstName={userFirstName} />
      ) : (
        <KioskLoginOptions consumerPhoneLoginUrl={consumerPhoneLoginUrl} phoneLoginUrl={phoneLoginUrl} />
      )}
    </StyledModalContainer>
  );
}

const StyledModalContainer = styled(ModalContainer)`
  @media ${KIOSK_PRO_MEDIA.maxWidth} {
    max-width: 600px;
    max-height: 660px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 0;
    justify-content: center;
    border-radius: 20px;
  }
`;
